import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, ExternalLink, Eye, PackageCheck, Plus, Trash2, Truck } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";
import {
  emptyOrderForm,
  formatMoney,
  normalizeOrder,
  orderTabs,
  paymentTone,
  statusTone,
} from "./orderUtils";

function shippingSummary(order) {
  if (!order?.trackingNumber) return "Tracking needed";
  const carrier = order.carrier || order.shippingAddress?.shippingLabel || order.shippingProvider || "";
  return [carrier, order.trackingNumber].filter(Boolean).join(" ");
}

const ORDER_FILTERS_KEY = "bobbinusa_admin_order_filters";

function readSavedFilters() {
  try {
    return JSON.parse(sessionStorage.getItem(ORDER_FILTERS_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function Order() {
  const navigate = useNavigate();
  const initialFilters = useMemo(readSavedFilters, []);
  const [activeTab, setActiveTab] = useState(initialFilters.activeTab || "needs_action");
  const [search, setSearch] = useState(initialFilters.search || "");
  const [country, setCountry] = useState(initialFilters.country || "");
  const [paymentFilter, setPaymentFilter] = useState(initialFilters.paymentFilter || "");
  const [channelFilter, setChannelFilter] = useState(initialFilters.channelFilter || "");
  const [carrierFilter, setCarrierFilter] = useState(initialFilters.carrierFilter || "");
  const [labelFilter, setLabelFilter] = useState(initialFilters.labelFilter || "");
  const [fromDate, setFromDate] = useState(initialFilters.fromDate || "");
  const [toDate, setToDate] = useState(initialFilters.toDate || "");
  const [selectedIds, setSelectedIds] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyOrderForm);
  const { dismiss, toast, toasts } = useToast();

  const loadOrders = useCallback(async () => {
    const data = await api.get(`/orders${activeTab === "archived" ? "?archived=true" : ""}`);
    return (data.orders || []).map(normalizeOrder);
  }, [activeTab]);

  const { data: orders, error, loading, reload, setData: setOrders } = useAdminResource(loadOrders, []);

  useEffect(() => {
    if (error && !loading) toast(`Could not load orders: ${error}`, "error");
  }, [error, loading, toast]);

  useEffect(() => {
    sessionStorage.setItem(ORDER_FILTERS_KEY, JSON.stringify({ activeTab, search, country, paymentFilter, channelFilter, carrierFilter, labelFilter, fromDate, toDate }));
  }, [activeTab, carrierFilter, channelFilter, country, fromDate, labelFilter, paymentFilter, search, toDate]);

  const visibleOrders = useMemo(() => {
    return orders.filter((order) => {
      const paymentAttention = (order.paymentStatus !== "paid" && !["cancelled", "returned", "delivered"].includes(order.status)) || order.fraudFlag;
      const readyToProcess = order.paymentStatus === "paid" && ["pending", "paid"].includes(order.status);
      const labelNeeded = order.paymentStatus === "paid" && ["pending", "paid", "processing"].includes(order.status) && !order.shippingLabelUrl && !order.trackingNumber;
      const statusMatches = {
        all: true,
        needs_action: labelNeeded || paymentAttention,
        payment_attention: paymentAttention,
        ready_to_process: readyToProcess,
        label_needed: labelNeeded,
        tracking_active: order.status === "shipped" && Boolean(order.trackingNumber),
        delivered: order.status === "delivered",
        cancelled_returned: ["cancelled", "returned"].includes(order.status),
        archived: true,
      }[activeTab] ?? false;
      const orderDate = order.createdAt ? new Date(order.createdAt) : null;
      const dateMatches = (!fromDate || (orderDate && orderDate >= new Date(`${fromDate}T00:00:00`)))
        && (!toDate || (orderDate && orderDate <= new Date(`${toDate}T23:59:59`)));
      const countryMatches = !country || String(order.shippingAddress?.country || "").toUpperCase() === country;
      const paymentMatches = !paymentFilter || order.paymentStatus === paymentFilter;
      const channelMatches = !channelFilter || order.channel === channelFilter;
      const carrierValue = String(order.carrier || order.shippingProvider || "").toLowerCase();
      const carrierMatches = !carrierFilter || carrierValue.includes(carrierFilter);
      const hasLabel = Boolean(order.shippingLabelUrl || order.shippoTransactionId);
      const labelMatches = !labelFilter || (labelFilter === "created" ? hasLabel : labelNeeded);
      const searchMatches = [
        order.orderNumber,
        order.user?.name,
        order.user?.email,
        order.channel,
        order.status,
        order.paymentStatus,
        order.trackingNumber,
      ].join(" ").toLowerCase().includes(search.toLowerCase());
      return statusMatches && dateMatches && countryMatches && paymentMatches && channelMatches && carrierMatches && labelMatches && searchMatches;
    });
  }, [activeTab, carrierFilter, channelFilter, country, fromDate, labelFilter, orders, paymentFilter, search, toDate]);

  const orderPagination = usePagination(visibleOrders, [activeTab, carrierFilter, channelFilter, country, fromDate, labelFilter, paymentFilter, search, toDate], 10);

  const resetFilters = () => {
    setSearch("");
    setCountry("");
    setPaymentFilter("");
    setChannelFilter("");
    setCarrierFilter("");
    setLabelFilter("");
    setFromDate("");
    setToDate("");
  };

  const metrics = {
    newOrders: orders.filter((order) => order.status === "pending").length,
    processing: orders.filter((order) => order.status === "processing").length,
    exceptions: orders.filter((order) => order.fraudFlag || order.paymentStatus !== "paid").length,
    shipped: orders.filter((order) => order.status === "shipped").length,
  };

  const updateCreateForm = (event) => {
    setCreateForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const toggleSelection = (orderId) => {
    setSelectedIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId]
    );
  };

  const createOrder = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        customerName: createForm.customerName,
        customerEmail: createForm.customerEmail,
        channel: createForm.channel,
        status: createForm.status,
        paymentStatus: createForm.paymentStatus,
        items: [{
          name: createForm.itemName,
          sku: createForm.itemSku,
          quantity: Number(createForm.quantity),
          price: Number(createForm.price),
        }],
        shipping: Number(createForm.shipping || 0),
        tax: Number(createForm.tax || 0),
        shippingAddress: {
          fullName: createForm.customerName,
          line1: createForm.line1,
          city: createForm.city,
          state: createForm.state,
          postalCode: createForm.postalCode,
          country: createForm.country,
        },
      };
      const data = await api.post("/orders", payload);
      setOrders((current) => [normalizeOrder(data.order), ...current]);
      setCreateForm(emptyOrderForm);
      setCreateOpen(false);
      toast(`Created order ${data.order.orderNumber}`, "success");
      navigate(`/dashboard/orders/${data.order._id}`);
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const bulkUpdate = async (status) => {
    if (!selectedIds.length) return;
    try {
      const count = selectedIds.length;
      await api.patch("/orders/bulk", { orderIds: selectedIds, status });
      setOrders((current) => current.map((order) => selectedIds.includes(order._id) ? { ...order, status } : order));
      setSelectedIds([]);
      toast(`Updated ${count} orders`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const bulkArchive = async () => {
    if (!selectedIds.length) return;
    const count = selectedIds.length;
    if (!window.confirm(`Archive ${count} selected order${count === 1 ? "" : "s"}?`)) return;
    try {
      await api.patch("/orders/bulk/archive", { orderIds: selectedIds });
      setOrders((current) => current.filter((order) => !selectedIds.includes(order._id)));
      setSelectedIds([]);
      toast(`Archived ${count} order${count === 1 ? "" : "s"}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const processOrder = async (order) => {
    try {
      const data = await api.patch(`/orders/${order._id}/process`, {});
      setOrders((current) => current.map((item) => item._id === order._id ? normalizeOrder(data.order) : item));
      toast(`Order ${order.orderNumber} moved to processing`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const archiveOrder = async (order) => {
    if (!window.confirm(`Archive order ${order.orderNumber}? It will be removed from active orders.`)) return;
    try {
      await api.patch(`/orders/${order._id}/archive`, {});
      setOrders((current) => current.filter((item) => item._id !== order._id));
      toast(`Archived order ${order.orderNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const restoreOrder = async (order) => {
    try {
      await api.patch(`/orders/${order._id}/restore`, {});
      setOrders((current) => current.filter((item) => item._id !== order._id));
      toast(`Restored order ${order.orderNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Permanently delete unpaid order ${order.orderNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${order._id}`);
      setOrders((current) => current.filter((item) => item._id !== order._id));
      toast(`Deleted order ${order.orderNumber}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const exportOrdersCsv = () => {
    const rows = [
      ["Order", "Customer", "Email", "Status", "Payment", "Channel", "Total", "Tracking"],
      ...visibleOrders.map((order) => [
        order.orderNumber,
        order.user?.name,
        order.user?.email,
        order.status,
        order.paymentStatus,
        order.channel,
        Number(order.total || 0).toFixed(2),
        [order.carrier, order.trackingNumber].filter(Boolean).join(" "),
      ]),
    ];
    const csv = rows.map((row) => row.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "orders.csv";
    link.click();
    URL.revokeObjectURL(url);
    toast("Orders CSV exported", "success");
  };

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Operational Core</p>
          <h1 className="text-3xl font-black text-gray-900">Orders</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Manage order intake, search, fulfillment, Shippo labels, pickups, tracking, packing slips, and invoice links from one page.
          </p>
        </div>
        <WorkflowToolbar
          actions={[
            { label: "Shipping Center", primary: true, onClick: () => navigate("/dashboard/shipping") },
            { label: createOpen ? "Close Create Order" : "Create Order", onClick: () => setCreateOpen((open) => !open) },
            { label: `Bulk Process (${selectedIds.length})`, onClick: () => bulkUpdate("processing") },
            { label: `Bulk Archive (${selectedIds.length})`, onClick: bulkArchive },
            { label: `Bulk Cancel (${selectedIds.length})`, onClick: () => bulkUpdate("cancelled") },
            { label: "Export CSV", onClick: exportOrdersCsv },
            { label: "Refresh", onClick: reload },
          ]}
        />
      </div>

      {createOpen && (
        <form className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm" onSubmit={createOrder}>
          <div className="mb-4 flex items-center gap-2">
            <Plus size={18} className="text-[#4391d1]" />
            <h2 className="text-lg font-black text-gray-900">Create Manual Phone / Email Order</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["customerName", "Customer name"], ["customerEmail", "Customer email"], ["itemName", "Item name"], ["itemSku", "Item SKU"],
              ["quantity", "Qty"], ["price", "Price"], ["shipping", "Shipping"], ["tax", "Tax"],
              ["line1", "Address"], ["city", "City"], ["state", "State"], ["postalCode", "Postal code"],
            ].map(([name, label]) => (
              <label className="block" key={name}>
                <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                <input
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                  name={name}
                  onChange={updateCreateForm}
                  required={["customerName", "customerEmail", "itemName", "quantity", "price", "line1", "city", "state"].includes(name)}
                  type={["quantity", "price", "shipping", "tax"].includes(name) ? "number" : "text"}
                  value={createForm[name]}
                />
              </label>
            ))}
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Channel</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="channel" onChange={updateCreateForm} value={createForm.channel}>
                <option value="direct">Direct</option><option value="facebook_marketplace">Facebook market place</option><option value="ebay">eBay</option><option value="bobbinusa">Bobbin USA</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Payment</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="paymentStatus" onChange={updateCreateForm} value={createForm.paymentStatus}>
                <option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="failed">Failed</option>
              </select>
            </label>
          </div>
          <button className="mt-5 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-bold text-white" type="submit">Create Order</button>
        </form>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <InsightCard title="New Orders" value={metrics.newOrders} note="Received and ready for review" />
        <InsightCard title="Processing" value={metrics.processing} note="Paid or packed orders in motion" tone="good" />
        <InsightCard title="Exceptions" value={metrics.exceptions} note="Fraud, payment, address, or stock issues" tone="warn" />
        <InsightCard title="Shipped" value={metrics.shipped} note="Tracking assigned and synced" />
      </div>

      <section className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <input
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] lg:max-w-md"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search order, customer, channel, status, tracking..."
            value={search}
          />
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-bold text-gray-600" onChange={(event) => setCountry(event.target.value)} value={country}>
              <option value="">All countries</option><option value="US">United States</option><option value="CA">Canada</option><option value="GB">United Kingdom</option><option value="AU">Australia</option>
            </select>
            <select className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-bold text-gray-600" onChange={(event) => setPaymentFilter(event.target.value)} value={paymentFilter}>
              <option value="">All payments</option><option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
            </select>
            <select className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-bold text-gray-600" onChange={(event) => setChannelFilter(event.target.value)} value={channelFilter}>
              <option value="">All channels</option><option value="bobbinusa">Bobbin USA</option><option value="direct">Direct</option><option value="ebay">eBay</option><option value="facebook_marketplace">Facebook Marketplace</option><option value="walmart">Walmart</option>
            </select>
            <select className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-bold text-gray-600" onChange={(event) => setCarrierFilter(event.target.value)} value={carrierFilter}>
              <option value="">All carriers</option><option value="usps">USPS</option><option value="ups">UPS</option><option value="fedex">FedEx</option><option value="dhl">DHL</option>
            </select>
            <select className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-bold text-gray-600" onChange={(event) => setLabelFilter(event.target.value)} value={labelFilter}>
              <option value="">All labels</option><option value="needed">Label needed</option><option value="created">Label created</option>
            </select>
            <label className="text-xs font-bold text-gray-400">From <input className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 font-semibold text-gray-600" onChange={(event) => setFromDate(event.target.value)} type="date" value={fromDate} /></label>
            <label className="text-xs font-bold text-gray-400">To <input className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 font-semibold text-gray-600" onChange={(event) => setToDate(event.target.value)} type="date" value={toDate} /></label>
            <button className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-xs font-black text-gray-600 hover:bg-gray-50" onClick={resetFilters} type="button">Reset filters</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {orderTabs.map((tab) => (
              <button className={`rounded-full px-4 py-2 text-xs font-black ${activeTab === tab.status ? "bg-[#4391d1] text-white" : "border border-gray-200 bg-white text-gray-600"}`} key={tab.status} onClick={() => setActiveTab(tab.status)} type="button">
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-black text-gray-900">Orders List</h2>
            <p className="mt-1 text-xs font-bold text-gray-400">{visibleOrders.length} visible orders</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-black text-gray-500">
            <PackageCheck size={15} /> Open an order to edit fulfillment details
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
              <tr>{["", "Order", "Status", "Payment", "Channel", "Customer", "Shipping", "Label", "Total", "Risk", "Action"].map((column) => <th className={`whitespace-nowrap px-6 py-4 font-black ${column === "Action" ? "sticky right-0 z-20 min-w-40 border-l border-gray-100 bg-gray-50" : ""}`} key={column}>{column}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan={11}>Loading orders...</td></tr>
              )}
              {!loading && !orderPagination.paginatedItems.length && (
                <tr><td className="px-6 py-8 text-center text-sm font-bold text-gray-400" colSpan={11}>No orders found.</td></tr>
              )}
              {orderPagination.paginatedItems.map((order) => (
                <tr className="group text-sm text-gray-600 hover:bg-blue-50/40" key={order._id}>
                  <td className="px-6 py-4"><input checked={selectedIds.includes(order._id)} onChange={() => toggleSelection(order._id)} type="checkbox" /></td>
                  <td className="px-6 py-4 font-black text-gray-900">{order.orderNumber}</td>
                  <td className="px-6 py-4"><StatusPill tone={statusTone(order.status)}>{order.status}</StatusPill></td>
                  <td className="px-6 py-4"><StatusPill tone={paymentTone(order.paymentStatus)}>{order.paymentStatus}</StatusPill></td>
                  <td className="px-6 py-4 capitalize">{order.channel}</td>
                  <td className="min-w-56 px-6 py-4"><div className="font-bold text-gray-900">{order.user?.name}</div><div className="text-xs text-gray-400">{order.user?.email}</div></td>
                  <td className="whitespace-nowrap px-6 py-4">{shippingSummary(order)}</td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {order.shippingLabelUrl ? (
                      <a className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-xs font-black text-[#4391d1]" href={order.shippingLabelUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </a>
                    ) : (
                      order.paymentStatus === "paid" ? (
                        <button
                          className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-3 py-2 text-xs font-black text-amber-700 hover:bg-amber-100"
                          onClick={() => navigate(`/dashboard/shipping?order=${order._id}`)}
                          type="button"
                        >
                          <Truck className="h-3.5 w-3.5" /> Create
                        </button>
                      ) : (
                        <span className="text-xs font-bold text-gray-400">Payment required</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4 font-bold">{formatMoney(order.total)}</td>
                  <td className="px-6 py-4">{order.fraudFlag ? <StatusPill tone="bad">Fraud flag</StatusPill> : <StatusPill tone="good">Clear</StatusPill>}</td>
                  <td className="sticky right-0 z-10 min-w-40 border-l border-gray-100 bg-white px-6 py-4 group-hover:bg-blue-50">
                    <div className="flex flex-wrap gap-2">
                      <button className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-3 py-2 text-xs font-black text-white" onClick={() => navigate(`/dashboard/orders/${order._id}`)} type="button">
                        <Eye size={14} /> View
                      </button>
                      {activeTab === "archived" ? (
                        <button className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-3 py-2 text-xs font-black text-green-700" onClick={() => restoreOrder(order)} type="button">Restore</button>
                      ) : (
                        <>
                          {order.paymentStatus === "paid" && order.status === "pending" && <button className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-xs font-black text-blue-700" onClick={() => processOrder(order)} type="button"><PackageCheck size={14} /> Process</button>}
                          <button className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-black text-gray-700" onClick={() => archiveOrder(order)} type="button"><Archive size={14} /> Archive</button>
                          {order.paymentStatus !== "paid" && !order.paypalCaptureId && <button className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-black text-red-700" onClick={() => deleteOrder(order)} type="button"><Trash2 size={14} /> Delete</button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={orderPagination} />
      </section>

    </div>
  );
}
